import { ObjectUtils, Utils, MinDuration, PromiseFlag } from '../main/ObjectUtils.js';
import { GlobalErrorHandler } from '../main/GlobalErrorHandler.js';

/**
 * The ProductEditor is a ViewModel for the part of the product's user interface which is responsible
 * for accepting edits by the user.
 */
export class ProductEditor {
  /**
   * This constructor is async because--if session storage contains a product--it will communicate
   * with the remote designerService to restore the product. This happens on the last line of the
   * constructor.
   */
  constructor(config, designerService, productService) {
    this.config = config;
    /** An object containing child objects with configuration for each edit field */
    this.fieldsCfg = config.fields || {};
    this.designerService = designerService;
    this.productService = productService;
    this.componentType = null; // Must be assigned by subclass
    this.designerUpdateFlag = new PromiseFlag(); // Used by addToCart click event handler

    // Debug:
    //const abortController = new AbortController();
    //this.designerService.getProductDetails(99, abortController.signal);

    const byId = id => document.getElementById(id);

    const elementIds = config.UI.El;
    this.elements = {
      productData: byId(elementIds.productData),
      designerSessionId: byId(elementIds.designerSessionId),
      productTitle: byId(elementIds.productTitle),
      shortDescription: byId(elementIds.shortDescription),
      sku: byId(elementIds.sku),
      grossPrice: byId(elementIds.grossPrice),
      netPrice: byId(elementIds.netPrice),
      discountPercent: byId(elementIds.discountPercent),
      resetBtn: byId(elementIds.resetBtn),
      productSpinner: byId(elementIds.productSpinner),
      errorContainer: byId(elementIds.errorContainer),
      errorMessage: byId(elementIds.errorMessage),
      addToCartBtn: byId(elementIds.addToCartBtn),
      productForm: byId(elementIds.productForm),
    };
    this.assignEditFieldElements(elementIds);

    if (this.isFieldVisible(this.fieldsCfg.quantity, false)) {
      this.elements.quantity = byId(elementIds.quantity);
      this.hasQuantity = true;
      this.addNumberInputEventHandlers(this.elements.quantity, (changedElement, changedValue) => {
        this.quantityChanged(changedValue);
      });
    }



    //this.popupEditorConfig = config.UI.popupEditor; // Must be JIT-assigned
    this.validValuesMap = config.validValuesMap;
    this.allFinishesMap = config.allFinishesMap;

    // Child element of addToCartBtn - separate element to avoid overwriting icons, etc. on the btn
    this.elements.addToCartBtnText = this.elements.addToCartBtn.querySelector('.text');

    this.elements.productForm.addEventListener('focusin', event => {
      // Removes the error message whenever the user focuses on an element, except when the currently
      // focused element is the one with an error.
      if (!event.target.validationMessage) {
        this.toggleErrorMessage(false);
      }
    });
    this.addPopupEditorBtnHandlers();
    //this.addPopupEditorHandlers(); // This is done JIT

    this.elements.productData.value = null;
    if (this.elements.designerSessionId) {
      this.elements.designerSessionId.value = null;
    }
    /* The product we're viewing and editing. Will have all the changes since we started or last
     * reset it. */
    this.product = {};
    /* True if the current product (this.product) is equal to the default product (this.config.defaultProduct). */
    this.isDefaultProduct = true;
    /* True if the current product (this.product) is equal to the saved product from the cart, if present
     * (this.config.savedProduct). If no cart, then saved and default products are the same: */
    this.isSavedProduct = true;
    /* Assigns the saved product from the cart (if present). If no cart, then saved and default
     * products are the same. This call should update the isDefault- and isSavedProduct properties. */
    this.assignProduct(this.config.savedProduct, false);
    /* Initialize origProduct as a clone of the product. origProduct is the state of the
     * product before the property changed timer starts. We compare the changed product property to
     * the origProduct's value to determine if we have a need to call the server with the changes. */
    this.resetOrigProduct();
    /* changedProduct contains the accumulated changes since the property changed timer started. */
    this.changedProduct = {};
    this.quantity = this.config.quantity;
    this.propertyChangedTimerId = null;
    // The number of ms. to wait from a property is changed until we call the Store API update method:
    this.propertyChangedUpdateDelay = 500;
    // Used for detecting committable input events on the number input edit boxes:
    this.isKeyDown = false;

    const resetBtn = this.elements.resetBtn;
    this.hasResetBtn = resetBtn !== null && resetBtn !== undefined;
    if (this.hasResetBtn) {
      resetBtn.onclick = () => {
        $(resetBtn).tooltip('hide'); // Because it seems to get stuck on screen after the click
        const content = this.config.resetBtnPopup;
        window.yesNoPopup.display(content.title, content.body,
          {
            yesBtnLabel: content.yes,
            noBtnLabel: content.no,
            onYes: (event) => {
              if (this.isPropertyChangedTimerActive()) {
                this.clearPropertyChangedTimer();
              }
            /*await*/ this.resetProduct(true, resetBtn);
            },
          });
      };
    }

    this.elements.addToCartBtn.onclick = async () => {
      this.disableAddToCartBtn(); // Prevent re-entrancy (double clicks, etc.)
      try {
        try {
          /* We must await because we want the updated product back before stringifying it below.
           * Also, we want to validate the latest change(s) to avoid passing invalid data to the
           * server. 12/27/2019. */
          await this.commitPropertyChangesIfPending(true, false);
        } catch (ex) {
          if (this.isValidationError(ex)) {
            /* We rely on the commitPropertyChanges method to having already displayed info about the
             * error to the user, and will swallow the error here; no need to report the error to the
             * server. But, we do need to skip the submit call below so just return now: */
            return;
          } else {
            throw ex; // Re-throw since this is an unknown exception
          }
        }
        // For passing to server and storing with order, etc.:
        this.elements.productData.value = JSON.stringify(this.product);
        if (this.elements.designerSessionId) {
          this.elements.designerSessionId.value = this.designerService.sessionId;
        }
        this.elements.productForm.submit();
      } finally {
        this.enableAddToCartBtn();
      }
    };

    /* If we are viewing a product from an order line, then use the query string (order id, order
     * line id, and transaction no.) as the id for the session storage object. Otherwise, use the
     * product id. */
    this.sessionStorageId = this.config.isOrderLine
      ? 'productEditor.product.' + window.location.search
      : 'productEditor.product.' + this.config.productId;
    this.NO_SESSION = 'NoSession';

    /* We have 3 event handlers to deal with different event life cycles on desktop and mobile
     * browsers. See https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
     * for more. There will be a minor overhead on modern browsers which handle all 3 events, but we'll
     * accept that. If we want to optimize we would need to introduce a product "dirty" flag. */
    window.addEventListener('beforeunload', () => {
      this.saveProductToSessionStorage();
    });
    window.addEventListener('pagehide', () => {
      this.saveProductToSessionStorage();
    });
    document.addEventListener('visibilitychange', () => {
      // fires when user switches tabs, apps, goes to homescreen, etc.
      if (document.visibilityState == 'hidden') {
        this.saveProductToSessionStorage();
      }
    });
    this.isPreviewDrawingLabelActive = false;

    this.isProductRestorePending = false; // Will be set to true when/if we see there's a product in the session storage
    /*await*/ this.restoreProductFromSessionStorage();
  }

  togglePreviewDrawingFinishLabels(on) {
    this.isPreviewDrawingLabelActive = on;
    if (this.onPreviewDrawingFinishLabelsToggled) {
      this.onPreviewDrawingFinishLabelsToggled(on);
    }
  }

  assignEditFieldElements(elementIds) {
    // To be overridden by subclasses
  }

  /**
   * Factory method used by the owner ProductPageViewModel.
   */
  createPopupEditor(editDrawing) {
    //return new MultiStepDoorFrontEditor(editDrawing, this.config.UI.popupEditor);
    // Must be overridden by subclasses!
  }

  /**
   * Click handler for the buttons which trigger the modal popup editors. We need the code in these
   * handlers to run before the popup event handlers (see the addPopupEditorHandlers() method) so
   * that we can prevent the popups from starting their display process in case of validation errors.
   */
  async handlePopupEditorBtnClick(event) {
    if (this.onNeedPopupEditor) {
      this.onNeedPopupEditor();
    }
    if (this.popupEditorDisplayed) {
      try {
        /* Commit any pending property changes and wait for updated product information to be returned
         * from the server. We must wait because 1) we want to display the updated product in the popup,
         * and 2) if there's an error (e.g. server-side validation error), then we don't want to display
         * the popup at all. 3/10/2021. */
        await this.commitPropertyChangesIfPending(true, false);

        // Ok, the update succeeded. Let's continue and display the popup editor:
        const btn = event.target;
        const popupId = btn.dataset.target;
        const popup = jQuery(popupId);
        // Pass needed data to the popup (is accessed in the popup's event handlers):
        popup.data('compType', btn.dataset.compType);
        popup.data('lastPopupElement', btn);
        popup.modal(); // Display the popup
      } catch (ex) {
        if (this.isValidationError(ex)) {
          /* The commitPropertyChanges method should already have displayed info about the error to
           * the user. We'll just swallow the exception; no need to report the error to the server. */
        } else {
          throw ex; // Re-throw since this is an unknown exception
        }
      }
    }
  }

  /** Adds button html elements to the buttons array parameter. */
  registerPopupEditorButtons(buttons) {
    // To be overridden by subclasses
  }

  /**
   * Assigns click handlers for the buttons which trigger modal popup editors. We need the code in
   * these handlers to run before the popup event handlers (see the addPopupEditorHandlers() method)
   * so that we can prevent the popups from starting their display process in case of validation errors.
   */
  addPopupEditorBtnHandlers() {
    let buttons = [];
    this.registerPopupEditorButtons(buttons);
    buttons.forEach(btn => {
      /* We are going to manually trigger the popup display in code in the handlePopupEditorBtnClick
       * event handler. Let's remove any data-toggle attribute on the button (that attribute causes
       * BootStrap to attach event handlers which will automatically display the popup). */
      delete btn.dataset.toggle;
      btn.onclick = async (event) => await this.handlePopupEditorBtnClick(event);
    });
  }

  /** Assigns BootStrap modal popup event handlers. */
  addPopupEditorHandlers() {
    // Can be overridden by subclasses!
    const popup = jQuery(this.popupEditorConfig.elements.modalPopup);

    popup.on('show.bs.modal', async (event) => {
      if (this.popupEditorDisplayed) {
        const typeId = popup.data('compType');
        this.editedComponentType = this.componentType[typeId];
        this.rebuildPopupEditor(this.editedComponentType);
      }
    });

    popup.on('shown.bs.modal', event => this.doOnPopupEditorDisplayed(event));

    popup.on('click', 'button[data-reason]', event => {
      this.lastPopupCloseReason = event.target.dataset.reason;
      //alert('click');
      //console.log(`Hiding popup, element was clicked: "${event.target}", reason is: "${this.lastPopupCloseReason}"`);
    });

    popup.on('hide.bs.modal', (event) => {
      //alert('hiding');
      /* Since Safari (at least on ios) doesn't update document.activeElement on button clicks, we can't rely on that.
       * Instead we handle the click event and update lastPopupReason there (see above). */
      //this.lastPopupCloseReason = null;
      //if (document.activeElement) {
      //  this.lastPopupCloseReason = document.activeElement.dataset.reason;
      //  console.log(`Hiding popup, document.activeElement is: "${document.activeElement}", reason is: "${this.lastPopupCloseReason}"`);
      //} else {
      //  console.log('Hiding popup, document.activeElement is undefined!');
      //}
    });

    popup.on('hidden.bs.modal', (event) => {
      //alert('hidden');
      //console.log(`Popup closed, reason: "${this.lastPopupCloseReason}"`);
      if (this.popupEditorClosed) {
        const lastElement = popup.data('lastPopupElement');
        this.popupEditorClosed(this, this.editedComponentType, this.lastPopupCloseReason === 'Ok', lastElement);
      }
    });
  }

  rebuildValidValuesMaps(validators) {
    // Must be overridden by subclasses!
  }

  /**
   * Helper method for rebuildPopupEditor. Updates the element's caption. If caption is null or empty,
   * then element's display will be set to none.
   * @param {any} mode If mode = 0, then the 'd-none' css class will be set if caption is unassigned.
   * If mode = 1, then the 'invisible' class will be set instead. If mode = 2, then no class will be set.
   */
  assignText(element, caption, clearWhenCaptionIsNull, mode = 0) {
    if (element) {
      if (caption) {
        element.textContent = caption;
      } else if (clearWhenCaptionIsNull) {
        element.textContent = null;
      }
      if (mode !== 2) {
        element.classList.remove('d-none', 'invisible');
        if (ObjectUtils.isNullOrEmpty(caption)) {
          if (mode === 0) {
            element.classList.add('d-none');
          } else if (mode === 1) {
            element.classList.add('invisible');
          }
        }
      }
    }
  }

  createSortedGalleryItems(componentType) {
    // Must be implemented by subclass!
  }

  createGalleryItemFragment(item, clickTarget, hideZoomIcon, zoomIconTooltip) {
    const elem = document.createElement('div');
    elem.classList.add('col', 'col-6', 'col-lg-4');
    const zoomIcon = hideZoomIcon ? '' : `<span class="zoom-target icon-expand" title="${zoomIconTooltip}"></span>`;
    elem.innerHTML = `<div data-key="${item.id}" class="gallery-item" data-appearance-key="${item.appearanceKey}">
        <span>${item.caption}</span><i class="icon-check"></i>
        <a href="${item.largeImgUrl}" data-size="${item.dataSize}" ${clickTarget}>
          <img class="img-thumbnail" src="${item.thumbImgUrl}" alt="${item.caption}">
          ${zoomIcon}
        </a>
      </div>`;
    return elem;
  }

  rebuildPopupEditor(componentType) {
    const config = this.popupEditorConfig;
    const popup = config.elements.popup;
    const compType = config.content[componentType.id];

    popup.title.innerHTML = compType.title;

    this.assignText(popup.largeScreen.drawingTitle, compType.drawingTitle.front, false, 2);
    // Initially "hide" the drawing title (use invisible instead of d-none to ensure drawing canvas doesn't jump around:
    popup.largeScreen.drawingTitle.classList.add('invisible');

    this.assignText(popup.smallScreen.drawingTitle, compType.drawingTitle.front, false, 2);
    // Initially "hide" the drawing title (use invisible instead of d-none to ensure drawing canvas doesn't jump around:
    popup.smallScreen.drawingTitle.classList.add('invisible');

    const rebuildEditScopeContainer = (screen) => {
      screen.editScopeContainer.classList.toggle('d-none', !componentType.useEditScope);
      this.assignText(screen.editScopeTitle, compType.editScopeTitle);
      this.assignText(screen.galleryTitle, compType.galleryTitle);

      if (screen.editScopeButtons && compType.editScopeButtons) {
        this.assignText(screen.editScopeButtons.btn1, compType.editScopeButtons.btn1);
        this.assignText(screen.editScopeButtons.btn2, compType.editScopeButtons.btn2);
        this.assignText(screen.editScopeButtons.btn3, compType.editScopeButtons.btn3);
      }
    };

    //popup.largeScreen.editScopeContainer.classList.toggle('d-none', !componentType.useEditScope);
    //this.assignText(popup.largeScreen.editScopeTitle, compType.editScopeTitle);
    //this.assignText(popup.largeScreen.galleryTitle, compType.galleryTitle);
    //this.assignText(popup.largeScreen.editScopeButtons.btn1, compType.editScopeButtons.btn1);
    //this.assignText(popup.largeScreen.editScopeButtons.btn2, compType.editScopeButtons.btn2);
    //this.assignText(popup.largeScreen.editScopeButtons.btn3, compType.editScopeButtons.btn3);

    //popup.smallScreen.editScopeContainer.classList.toggle('d-none', !componentType.useEditScope);
    //this.assignText(popup.smallScreen.editScopeTitle, compType.editScopeTitle);
    //this.assignText(popup.smallScreen.galleryTitle, compType.galleryTitle);
    //this.assignText(popup.smallScreen.editScopeButtons.btn1, compType.editScopeButtons.btn1);
    //this.assignText(popup.smallScreen.editScopeButtons.btn2, compType.editScopeButtons.btn2);
    //this.assignText(popup.smallScreen.editScopeButtons.btn3, compType.editScopeButtons.btn3);
    if (config.showEditScopeStep) {
      rebuildEditScopeContainer(popup.largeScreen);
      rebuildEditScopeContainer(popup.smallScreen);
    }

    const clickTarget = 'data-target=".vp-editor .carousel" data-slide="next"';
    const largeScreenClickTarget = popup.largeScreen.slideOnFinishClick ? clickTarget : '';
    const smallScreenClickTarget = popup.smallScreen.slideOnFinishClick ? clickTarget : '';
    const zoomIconTooltip = config.content.zoomIconTooltip;

    const largeScreenMenu = popup.largeScreen.$galleryFilterMenu[0];
    const smallScreenMenu = popup.smallScreen.$galleryFilterMenu[0];
    const hide = !componentType.showGalleryFilterButton;
    largeScreenMenu.classList.toggle('d-none', hide);
    if (smallScreenMenu) {
      smallScreenMenu.classList.toggle('d-none', hide);
    }

    const sortedValues = this.createSortedGalleryItems(componentType);
    ObjectUtils.clearChildren(popup.largeScreen.gallery);
    ObjectUtils.clearChildren(popup.smallScreen.gallery);
    const largeScreenDom = document.createDocumentFragment();
    const smallScreenDom = document.createDocumentFragment();
    sortedValues.forEach(item => {
      if (item) {
        largeScreenDom.appendChild(this.createGalleryItemFragment(item, largeScreenClickTarget,
          compType.hideZoomIcon, zoomIconTooltip));
        smallScreenDom.appendChild(this.createGalleryItemFragment(item, smallScreenClickTarget,
          compType.hideZoomIcon, zoomIconTooltip));
      }
    });
    popup.largeScreen.gallery.appendChild(largeScreenDom);
    popup.smallScreen.gallery.appendChild(smallScreenDom);
  }

  createSortedValues(validValues, allValuesMap) {
    const sortedValues = Array(validValues.length);
    let index = 0;
    validValues.forEach(id => {
      sortedValues[index] = allValuesMap[id];
      if (sortedValues[index]) {
        sortedValues[index].id = id;
        index++;
      } else {
        console.warn(`${id} in the validValues array was not found in the allValuesMap`);
      }
    });
    // TODO: Add current web site's culture to the localeCompare function
    sortedValues.sort((a, b) => a.caption.localeCompare(b.caption));
    return sortedValues;
  }

  doOnPopupEditorDisplayed() {
    this.lastPopupCloseReason = null;
    if (this.popupEditorDisplayed) {
      this.popupEditorDisplayed(this, this.popupEditorConfig, this.editedComponentType);
    }
    this.popupEditorChangeType = {
      isAtomic: false,
    }
  }

  /**
   * When the popup editors are committed, the event handling is delegated to the owner ProductPageViewModel
   * which will first update the page, then call us back (this method).
   */
  async updateProductFromPopupEditor(modifiedProduct, componentType, changedElement) {
    //console.log(`Updating product from popup editor. Modified product: "${JSON.stringify(modifiedProduct)}"`);
    if (modifiedProduct) {
      if (this.popupEditorChangeType.isAtomic) {
        /* This means we should not send all changes (on the product's components) to the server.
         * Instead we'll just send the atomic change on the root product. */
        const apiPropertyName = this.popupEditorChangeType.propertyName;
        const changedValue = modifiedProduct[apiPropertyName];
        // Update the editor with the atomic change to the root, this will cause a smaller change to be sent to the server:
        this.propertyChanged(apiPropertyName, changedValue, changedElement);
      } else {
        // Assign all the changes on the modified product (including on components); all changes will be sent to the server:
        this.changedProduct = modifiedProduct;
        /* Note, we're not handling any possible validation errors inside the call.
         * At this point we assume validation has been done inside the popup editor.
         * If we later find that validation cannot be done by the popup editor, then
         * we must wrap the call below in a try/catch phrase, and decide which errors
         * to handle, and which ones to pass on (to the global error handler). 3/9/2021. */
        await this.commitPropertyChanges(false, false, changedElement);
      }
    }
  }

  /**
   * This method is async because--if session storage contains a product--it will communicate
   * with the remote designerService.
   */
  async restoreProductFromSessionStorage() {
    const dataStr = sessionStorage.getItem(this.sessionStorageId);
    if (dataStr && dataStr !== '') {
      /* If we have just switched region by choosing a different "flag", then the product in the
       * session storage contains the market code from the old region while we actually want to
       * use the market code from the current region (which is what's in the product right now).
       * Hence, replace the product (with the one from session storage), but don't replace the
       * market code. 3/11/2021. */
      const data = JSON.parse(dataStr);
      const product = data.p || data; // "|| data" is for backwards compatibility. Can be removed later.
      const quantity = data.q || 1; // "|| 1" is for backwards compatibility. Can be removed later.
      this.replaceProduct(product, /*replaceMktCode:*/ false);
      this.quantity = quantity;
      this.resetOrigProduct();
      this.changedProduct = {};
      this.isProductRestorePending = true;
      try {
        console.log('(await) ProductEditor: Restoring product from session storage...');
        // Must use await to enable exception handling.
        await this.commitPropertyChanges(false, true, null);
      } catch (ex) {
        this.toggleErrorMessage(false);
        // Swallow the exception, but report it:
        GlobalErrorHandler.HandleException(ex);
        /* If an error happens on processing the data from the session storage, then (most likely)
         * the only way to continue is to reset to the default product: */
        /*await*/ this.resetProduct(false); // Note, no catch handler for aborts here
      } finally {
        this.isProductRestorePending = false;
        console.log('ProductEditor: isProductRestorePending = false');
      }
    }
  }

  saveProductToSessionStorage() {
    if (this.isSavedProduct && !this.isQuantityChanged()) {
      sessionStorage.removeItem(this.sessionStorageId);
    } else {
      const obj = { p: this.product, q: this.quantity };
      const data = JSON.stringify(obj);
      sessionStorage.setItem(this.sessionStorageId, data);
    }
  }

  addNumberInputEventHandlers(element, onChanged) {
    /* The purpose of these event handlers is to trigger a change event not only when the user
     * leaves the field, but also when he clicks the up/down arrows. Normally, clicks on the up/down
     * arrows wouldn't trigger the change event. */
    element.addEventListener('keydown', () => {
      this.isKeyDown = true;
    });
    element.addEventListener('keyup', () => {
      this.isKeyDown = false;
    });
    element.addEventListener('input', (event) => {
      if (this.isKeyDown) {
        this.isKeyDown = false;
        if (this.config.canEditOrderLine) {
          /* The user just started editing the value, but has not committed anything yet. At this
           * point we want to update the cart button to let the user know that clicking it will save
           * the not yet committed changes in the input field. 12/27/2019 */
          this.elements.addToCartBtnText.textContent = this.config.saveAndReturnToCartLabel;
        }
      } else {
        //console.log(event.target.id + ' changed (input event)...');
        onChanged(event.target, parseFloat(event.target.value));
      }
    });
    element.addEventListener('change', (event) => {
      //console.log(event.target.id + ' changed (change event)...');
      onChanged(event.target, parseFloat(event.target.value));
    });
  }

  addStringRadioListEventHandlers(parentElement, onChanged) {
    // We'll add the listener to the parent element since the radio items will be updated at runtime
    parentElement.addEventListener('change', (event) => {
      //console.log(event.target.id + ' changed (change event)...');
      onChanged(parentElement, event.target.value);
    });
  }

  addNumberRadioListEventHandlers(parentElement, onChanged) {
    // We'll add the listener to the parent element since the radio items will be updated at runtime
    parentElement.addEventListener('change', (event) => {
      //console.log(event.target.id + ' changed (change event)...');
      onChanged(parentElement, parseFloat(event.target.value));
    });
  }

  /**
   * We assume this is a Bootstrap style dropdown with a clickable button or anchor tag, followed
   * by a sibling div with one or more child elements representing the items of the dropdown.
   */
  addDropdownListEventHandlers(buttonElements, onChanged) {
    if (buttonElements instanceof HTMLElement) {
      buttonElements = [buttonElements]; // Make it an array
    }
    for (const buttonElement of buttonElements) {
      const menu = buttonElement.parentElement.querySelector('.dropdown-menu');
      // We'll simplify by adding the listener to the parent element:
      menu.addEventListener('click', (event) => {
        if (event.target.name) {
          onChanged(buttonElement, event.target.name);
        } else {
          // Assume the click was on the border of the parent div or something similar
        }
      });
    }
  }

  //#region Handling changed product properties....................

  formatCurrency(value, language, currency, maxDecimals) {
    language = language ? language : this.config.language;
    currency = currency ? currency : this.config.currency;
    const options = { style: 'currency', currency: currency };
    options.minimumFractionDigits = 0;
    if (value == Math.trunc(value)) {
      options.maximumFractionDigits = 0;
    } else if (typeof maxDecimals !== typeof undefined) {
      options.maximumFractionDigits = maxDecimals;
    }
    return value.toLocaleString(language, options);
  }

  getGrossPrice(product, includeVat) {
    let withVat = product.price * (1 + this.config.vatRate);
    if (withVat >= 100) {
      // This matches the same code in the backend c#
      withVat = ObjectUtils.round(withVat);
    }
    if (includeVat) {
      return withVat;
    } else {
      return withVat / (1 + this.config.vatRate);
    }
  }

  getNetPriceRate() {
    return (100 - this.config.discountPercent) / 100;
  }

  /**
   * Returns the text of the first label element associated with the input element, or defaultValue
   * if none found.
   */
  getInputLabel(input, defaultValue) {
    let label = defaultValue;
    if (input.labels && input.labels.length > 0) {
      label = input.labels[0].firstChild.wholeText;
      if (!label && input.labels.length > 1) {
        label = input.labels[1].firstChild.wholeText;
      }
    }
    return label ? label.trim() : null;
  }

  /**
   * Helper function for the displayNumberInput and assignValidValue methods.
   * @param {any} log A text string containing all changes. Is updated inside this function.
   * @param {any} label A label in front of the change.
   */
  addToLog(log, label, oldValue, newValue) {
    log.text += `<li><b>${label}</b>: ${oldValue} --> ${newValue}</li>`;
  }

  /**
   * Helper method for the input fields setup. Returns true if the field's isDisplayed property is
   * true, or if the field is undefined, or if the isDisplayed is undefined.
   * @param {any} field A product editor field configuration.
   */
  isFieldVisible(field, defaultValue = true) {
    // Return true by default if the field or the isDisplayed property has not been defined
    if (!field || (field.isDisplayed === undefined)) {
      return defaultValue;
    }
    return field.isDisplayed;
  }

  displayNumberInput(input, value, validator, storeValidator, changeLog, defaultLogLabel, changedElement, readOnly) {
    // Validator from Langlo Designer:
    validator = validator || {};
    // If validator is undefined then keep the original values (3/9/2021):
    let min = validator.min || input.min; //0;
    let max = validator.max || input.max; //999999;

    // Validator from our CMS:
    storeValidator = storeValidator || {};
    const storeMin = storeValidator.min || 0;
    const storeMax = storeValidator.max || 999999;

    /* Use the maximum min value of the 2 validators, and the mimimum max value.
     * Although the validator might allow decimals, the current UI design (the input html element)
     * does not support it. We'll make the assumption that the nearest (ceil/floor) number is
     * acceptable. 12/10/2020. */
    min = Math.max(Math.ceil(min), Math.ceil(storeMin));
    max = Math.min(Math.floor(max), Math.floor(storeMax));

    if (min !== input.min || max !== input.max) {
      input.min = min;
      input.max = max;
      // Update the text below the input with validity information:
      const units = input.dataset.units || 'mm';
      const range = `${input.min} ${units} - ${input.max} ${units}`;
      const element = input.parentElement.querySelector('label.validity-info');
      if (element) {
        element.textContent = range;
      }
      // Update the Info popup with validity information:
      const infoSpan = input.parentElement.querySelector('span.info');
      if (infoSpan) {
        let content = infoSpan.dataset.content;
        const eolPos = content.indexOf('\n');
        if (eolPos > 0) {
          infoSpan.dataset.content = range + content.substr(eolPos);
        }
      }
    }
    input.readOnly = readOnly;
    if (value != input.value) {
      if (changeLog && input != changedElement) {
        // Update the change log:
        const logLabel = this.getInputLabel(input, defaultLogLabel);
        this.addToLog(changeLog, logLabel, input.value, value);
      }
      input.value = value;
    }
  }

  displayRadioListInput(input, value, validator, storeValidator, changeLog, defaultLogLabel,
    changedElement, displayValues) {
    // Save the current value:
    const selectedItem = input.querySelector('input[type="radio"]:checked');
    const oldValue = selectedItem ? selectedItem.value : null;

    const validValues = Utils.getArrayIntersect(
      validator ? validator.values : null,
      storeValidator ? storeValidator.values : null);

    if (validValues) {
      // Rebuild the list of radio items based on the validator's values:

      // First, remove all existing items:
      const radioItemDivs = input.querySelectorAll('.custom-radio');
      radioItemDivs.forEach(item => input.removeChild(item));

      // Get the display values:
      let actualDisplayValues;
      if (displayValues) {
        actualDisplayValues = [];
        validValues.forEach(item => {
          actualDisplayValues.push(displayValues[item] || item);
        });
      } else {
        actualDisplayValues = validValues;
      }

      // Then add new items:
      validValues.forEach((item, index) => {
        const isChecked = item == value;
        const radioId = input.id + '_' + index;
        const checked = isChecked ? 'checked' : null;
        const div = document.createElement('div');
        div.classList.add('custom-control', 'custom-radio');
        div.innerHTML =
          `<input class="custom-control-input" type="radio" id="${radioId}" name="${input.id}"
                value="${item}" ${checked} />
          <label class="custom-control-label" for="${radioId}">${actualDisplayValues[index]}</label>`;
        input.appendChild(div);
      });
    } else {
      // We don't have a validator. Let's try to find the radio item matching the new value, and
      // set its checked attribute:
      const radioItems = input.querySelectorAll('.custom-radio input');
      radioItems.forEach(item => {
        if (item.value == value) {
          item.checked = true;
        }
      });
    }

    // "!=" on the next line; we want to be able to compare strings to numbers, e.g. 500 != "500"
    if (value != oldValue) {
      if (changeLog && input !== changedElement) {
        // Update the change log:
        const label = input.querySelector('label');
        const logLabel = label ? label.innerText.trim() : defaultLogLabel;
        this.addToLog(changeLog, logLabel, oldValue, value);
      }
      //input.value = value; Done above!
    }
  }

  /**
   * Update the dropdown list's element with new content and update which item is the currently
   * active one. We assume this is a Bootstrap style dropdown, and we assume the "active" class
   * name is associated with the selected/active item. We also assume the product property value
   * associated with each item is stored as the item's name attribute.
   * @param {any} buttonElement The button or anchor element which is clicked to drop down the list
   * @param {any} value The new value (product value).
   * @param {any} changeLog For tracking side effects when changing values
   * @param {any} defaultLogLabel
   * @param {any} changedElement The element the user originally changed (could be different than this one)
   * @param {any} displayValues We need to update the button content with one of these values
   */
  displayDropdownList(buttonElement, value, changeLog, defaultLogLabel, changedElement, displayValues) {
    const menu = buttonElement.parentElement.querySelector('.dropdown-menu');
    // Save the current value:
    let activeItem = menu.querySelector('.dropdown-item.active');
    const oldValue = activeItem ? activeItem.name : null;

    // "!=" on the next line; we want to be able to compare strings to numbers, e.g. 1 != "1"
    if (value != oldValue) {
      if (activeItem) {
        activeItem.classList.remove('active');
      }
      activeItem = menu.querySelector(`a[name='${value}']`);
      if (activeItem) {
        activeItem.classList.add('active');
      }
      let content = null;
      if (displayValues) {
        /* displayValues is an object whose property names correspond to the product property's valid
         * values and the object property values contain the display values. */
        content = displayValues[value];
      } else if (activeItem) {
        content = activeItem.text;
      }

      if (changeLog && buttonElement !== changedElement) {
        // Update the change log:
        const logLabel = this.getInputLabel(buttonElement, defaultLogLabel);
        this.addToLog(changeLog, logLabel, buttonElement.textContent, content);
      }
      buttonElement.textContent = content;
    }
  }

  /**
   * Finds the human readable text associated with the value parameter and updates the element's
   * text content. If content has changed, then also update the change log.
   */
  assignValidValue(element, map, value, changeLog, defaultLogLabel, changedElement) {
    if (element) {
      const content = map[value] ? map[value].caption.trim() : null;
      if (content !== element.textContent.trim()) {
        if (changeLog && element != changedElement) {
          const logLabel = this.getInputLabel(element, defaultLogLabel);
          this.addToLog(changeLog, logLabel, element.textContent, content);
        }
        element.textContent = content;
      }
    }
  }

  showMessagePopup(title, html, options) {
    if (this.config.onDisplayPopup) {
      this.config.onDisplayPopup(title, html, options);
    } else if (window.okPopup) {
      window.okPopup.display(title, html, options);
    } else {
      alert(`${title}\n\n${html}`);
    }
  }

  doDisplayProduct(product, validators, changedElement, changeLog) {
    if (this.isFieldVisible(this.fieldsCfg.quantity, false)) {
      const validator = validators ? validators.quantity : null;
      this.displayNumberInput(this.elements.quantity, this.quantity, validator,
        this.fieldsCfg.quantity, changeLog, 'Quantity', changedElement);
    }
  }

  refreshResetBtn() {
    if (this.isSavedProduct && !this.isQuantityChanged()) {
      this.disableResetBtn();
      if (this.config.canEditOrderLine) {
        this.elements.addToCartBtnText.textContent = this.config.returnToCartLabel;
      }
    } else {
      this.enableResetBtn();
      if (this.config.canEditOrderLine) {
        this.elements.addToCartBtnText.textContent = this.config.saveAndReturnToCartLabel;
      }
    }
  }

  displayProduct(product, validators, changedElement) {
    const isUserDriven = changedElement && changedElement != null;
    const changeLog = isUserDriven ? { text: '' } : null;

    this.refreshResetBtn();
    this.doDisplayProduct(product, validators, changedElement, changeLog);

    if (isUserDriven && changeLog.text) {
      const suppressChangeMsg = changedElement && changedElement.dataset.suppressChgMsg;
      if (!suppressChangeMsg) {
        const prompt = `<b>${this.config.changeLogSubTitle}:</b><br/><br/>`;
        const list = `<ul>${changeLog.text}</ul>`;
        this.showMessagePopup(this.config.changeLogTitle, prompt + list);
      }
    }
  }

  displayPrice(product) {
    const grossPriceWithVat = this.getGrossPrice(product, true);
    if (this.elements.grossPrice) {
      this.elements.grossPrice.textContent = this.formatCurrency(grossPriceWithVat);
    }
    if (this.elements.netPrice) {
      const netPriceWithVat = grossPriceWithVat * this.getNetPriceRate();
      // The last param says "round to 0 decimals"
      this.elements.netPrice.textContent = this.formatCurrency(netPriceWithVat, undefined, undefined, 0);
    }
  }

  displayProductDetails(details, changedElement) {
    this.displayPrice(details.product);
    this.displayProduct(details.product, details.validators, changedElement);
  }

  equalProducts(a, b) {
    if (a === undefined && b === undefined) {
      return true;
    }
    // The codeVer property should not be considered when comparing the products.
    const savedCodeVer = b.codeVer;
    b.codeVer = a.codeVer;
    try {
      return ObjectUtils.equalContents(a, b);
    } finally {
      b.codeVer = savedCodeVer;
    }
  }
  productChanged(isUserDriven) {
    this.isDefaultProduct = this.equalProducts(this.product, this.config.defaultProduct);
    // The saved product from the cart (if present). If no cart, then saved and default products are the same:
    this.isSavedProduct = this.equalProducts(this.product, this.config.savedProduct);
    if (this.onProductChanged) {
      this.onProductChanged(this, isUserDriven);
    }
  }
  replaceProduct(newProduct, replaceMktCode = true) {
    if (newProduct !== this.product) {
      const oldMktCode = this.product.mktCode;
      this.product = newProduct;
      if (!replaceMktCode) {
        this.product.mktCode = oldMktCode;
      }
      this.productChanged();
    }
  }
  assignProduct(source, isUserDrivenChange) {
    this.product = ObjectUtils.clone(source, true);
    this.productChanged(isUserDrivenChange);
  }
  resetOrigProduct() {
    this.origProduct = ObjectUtils.clone(this.product, true);
  }

  /* Updates technical specification in the UI with the latest live values. The formattedSpec
   * parameter is a techspec object whose values are formatted string values. */
  updateTechSpec(formattedSpec) {
    /* We assume techSpecValues is a NodeList with id and textContent. Each id should match one of
     * the properties of the formattedSpec object. */
    const elements = this.config.UI.techSpecValues;
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const value = formattedSpec[element.id];
      element.textContent = value;
      // Toggle display:none on the parent <tr> if the value is undefined
      element.parentElement.classList.toggle('d-none', !value);
    }
  }

  /* Is called from commitPropertyChanges() and resetProduct() */
  productDetailsChanged(details, changedElement, isReset) {
    const isUserDriven = isReset || (changedElement && changedElement != null);
    this.assignProduct(details.product, isUserDriven);
    //this.lastProductValidators = details.validators;
    this.rebuildValidValuesMaps(details.validators);
    // Update the UI (DOM):
    if (this.config.isDesignerProduct) {
      this.elements.productTitle.innerHTML = this.isDefaultProduct || this.config.keepOriginalTexts
        ? this.config.productTitle
        : this.config.overriddenTitle;
      this.elements.shortDescription.innerHTML = this.isDefaultProduct || this.config.keepOriginalTexts
        ? this.config.shortDescription
        : this.config.overriddenShortDescription;
    } else if (details.uiData) {
      this.elements.productTitle.innerHTML = details.uiData.productTitle;
      this.elements.shortDescription.innerHTML = details.uiData.shortDescription;
      this.elements.sku.innerHTML = details.uiData.sku;
    }
    this.displayProductDetails(details, changedElement);
    this.changedProduct = {};
    this.resetOrigProduct();

    if (this.config.isDesignerProduct) {
      this.productService.getFormattedTechSpec(this.config.langReg, details.techSpec, this.abortController.signal)
        .then(formattedTechSpec => {
          this.updateTechSpec(formattedTechSpec);
        })
        .catch(ex => {
          if (ex.name === 'AbortError') {
            /* A promise (i.e. fetch call) was aborted (see AbortController.abort() method). At this
             * point the only use case is when the user makes another change before we've received a
             * response from the server for the previous change. 3/9/2021. */
            console.log('Getting formatted tech. spec. was aborted due to user making more changes!');
          } else {
            console.error('Unknown error during call to productService.getFormattedTechSpec: ', ex);
            // Swallow the exception, but report it:
            GlobalErrorHandler.HandleException(ex);
          }
        });
    }
  }

  findSpinnerTarget(origParent) {
    let parent = origParent;
    let target = null;
    while (parent && !target) {
      target = parent.querySelector('label[data-is-spinner-target="1"]');
      parent = parent.parentElement;
    }
    return target ? target.parentElement : origParent;
  }

  toggleProductSpinner(on, changedElement) {
    if (on) {
      // Move the spinner to a position relative to the changed element:
      const element = changedElement || this.elements.productSpinner;
      const newParent = this.findSpinnerTarget(element.parentElement);
      if (newParent !== this.elements.productSpinner.parentElement) {
        newParent.appendChild(this.elements.productSpinner);
      }
      // Ensure it's visible:
      this.elements.productSpinner.classList.remove('invisible');
      // Use helper class to ensure the spinner is visible for at least 1 second (avoid "blink-of-the-eye" spinner)
      this.spinnerMinDuration = new MinDuration(300, () => this.elements.productSpinner.classList.add('invisible'));
    } else if (this.spinnerMinDuration) {
      // This will hide the spinner by calling the callback added to MinDuration above, but not until x ms have elapsed:
      this.spinnerMinDuration.complete();
    }
  }

  toggleErrorMessage(on, message) {
    this.elements.errorMessage.textContent = message ? message : null;
    if (on) {
      this.elements.errorContainer.classList.remove('d-none');
    } else {
      this.elements.errorContainer.classList.add('d-done');
    }
  }

  /** This method is async because it communicates with the remote designerService. */
  async commitPropertyChanges(validate, submitFullProduct, changedElement, spinnerElement) {
    //console.log('Timer expired...');
    if (validate && !this.elements.productForm.reportValidity()) {
      // We have a client side validation error!!!
      const errorMsg = (changedElement ? changedElement.validationMessage : null) || this.config.invalidValueMessage;
      this.toggleErrorMessage(true, errorMsg);
      // Restore product and ensure restored values are displayed
      this.assignProduct(this.origProduct, false);
      this.displayProduct(this.product);
      this.changedProduct = {};
      /* Note, the error thrown here must either be handled by the caller, or allow it to be sent
       * to the global error handler (i.e. reported to the server). 3/9/2021. */
      throw new ValidationException(errorMsg);
    }
    let details = null;
    this.toggleProductSpinner(true, spinnerElement || changedElement);
    this.designerUpdateFlag.clear(); // Allows us to wait for update completion before adding to cart
    try {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      if (!this.changedProduct.type) {
        this.changedProduct.type = this.product.type;
      }
      if (!this.config.isDesignerProduct) {
        details = await this.productService.getVariantProductDetails(this.config.productId, this.product.variantId, signal);
      } else if (submitFullProduct) {
        /* Note, as of 9/17/2020, the only time submitFullProduct is true is when restoring the
         * product from session storage. */
        details = await this.designerService.updateProduct(this.product, signal);
      } else {
        details = await this.designerService.updateProductValues(this.changedProduct, signal);
        /* I'm not sure if the capitalized "Message" was a bug before, or if the serialization on the
         * server has changed recently. To avoid issues, I'm testing for both (lower case "message")
         * seems to be correct from now on, at least in my local env. 8/4/2022. */
        const noSession = (details.Message && details.Message === this.NO_SESSION) || (details.message && details.message === this.NO_SESSION);
        if (noSession) {
          /* We need to give the server both the original product, and the change(s) made by the user: */
          details = await this.designerService.updateProductPlusDelta(this.origProduct, this.changedProduct, signal);
        }
      }
      //console.log('Received updated product details:');
      //console.log(details);
      this.productDetailsChanged(details, changedElement);
    } catch (ex) {
      if (ex.name === 'AbortError') {
        /* A promise (i.e. fetch call) was aborted (see AbortController.abort() method). At this
         * point the only use case is when the user makes another change before we've received a
         * response from the server for the previous change. 3/9/2021. */
        console.log('Designer Service call was aborted due to user making more changes!');
      } else {
        if (this.isValidationError(ex)) {
          this.toggleErrorMessage(true, this.config.invalidValueMessage); //'Invalid value!'
        } else if (this.containsError(ex.errorInfo, true,
          error => error.ExceptionType && error.ExceptionType.includes('UnableToMoveComponent'))) {
          // TODO: Localize
          this.toggleErrorMessage(true, `Vi var dessverre ikke i stand til å endre produktet som ønsket.`);
        } else {
          this.toggleErrorMessage(true, this.config.genericErrorMessage); //'Sorry! Something unexpected happened in the app.'
        }
        // Recover from the exception - ensure displayed product values are restored
        this.assignProduct(this.origProduct, false);
        const validator = details ? details.validators : null;
        this.displayProduct(this.product, validator);
        this.changedProduct = {};
        throw ex; // Since this is an async method, the caller must use await and catch the exception to avoid it being unhandled
      }
    } finally {
      this.designerUpdateFlag.set();
      this.toggleProductSpinner(false, spinnerElement || changedElement);
    }
  }

  containsError(errorInfo, recursive, predicate) {
    if (errorInfo) {
      if (predicate(errorInfo)) {
        return true;
      } else if (recursive) {
        return this.containsError(errorInfo.InnerException, recursive, predicate);
      }
    }
    return false;
  }

  isValidationError(ex) {
    return ex instanceof ValidationException ||
      this.containsError(ex.errorInfo, true,
        error => error.ExceptionType && error.ExceptionType.includes('Validation'));
  }

  /**
   * This method is used in cases where we need to commit any pending changes before
   * continuing, e.g. if we need to get updated product validators back from the server.
   * It's the caller's responsibility to handle exceptions thrown inside the call, e.g.
   * validation errors.
   */
  async commitPropertyChangesIfPending(validate, submitFullProduct) {
    if (this.isPropertyChangedTimerActive()) {
      this.clearPropertyChangedTimer();
      await this.commitPropertyChanges(validate, submitFullProduct, this.propertyChangedElement,
        this.lastSpinnerElement);
    }
    // Wait for any updates to the remote Designer service to complete before returning:
    await this.designerUpdateFlag.hasBeenSet();
  }

  isPropertyChangedTimerActive() {
    return this.propertyChangedTimerId != null;
  }

  clearPropertyChangedTimer() {
    clearTimeout(this.propertyChangedTimerId);
    this.propertyChangedTimerId = null;
  }

  disableButton(button) {
    button.setAttribute('disabled', ''); // Yes, this disables it
  }

  enableButton(button) {
    button.removeAttribute('disabled');
  }

  disableAddToCartBtn() {
    this.disableButton(this.elements.addToCartBtn);
  }

  enableAddToCartBtn() {
    this.enableButton(this.elements.addToCartBtn);
  }

  disableResetBtn() {
    if (this.hasResetBtn) {
      this.disableButton(this.elements.resetBtn);
    }
  }

  enableResetBtn() {
    if (this.hasResetBtn) {
      this.enableButton(this.elements.resetBtn);
    }
  }

  invalidatePropertyChangedTimer(changedElement, spinnerElement) {
    if (this.isPropertyChangedTimerActive()) {
      //console.log('Clearing timer...');
      this.clearPropertyChangedTimer();
    }
    if (ObjectUtils.hasProperties(this.changedProduct)) {
      //console.log('Setting timer...');
      this.propertyChangedElement = changedElement;
      this.lastSpinnerElement = spinnerElement;
      this.disableAddToCartBtn(); // Don't allow adding to cart until the changes are committed
      this.propertyChangedTimerId = setTimeout(async () => {
        // We use the timer to allow the user to make multiple changes before they are submitted to the server
        this.clearPropertyChangedTimer();
        this.propertyChangedElement = null;
        this.lastSpinnerElement = null;
        try {
          await this.commitPropertyChanges(true, false, changedElement, spinnerElement);
        } catch (ex) {
          if (this.isValidationError(ex)) {
            /* We rely on the commitPropertyChanges method to having already displayed info
             * about the error to the user, and will swallow the error here;
             * no need to report the error to the server. */
          } else {
            throw ex; // Re-throw since this is an unknown exception
          }
        } finally {
          this.enableAddToCartBtn();
        }
      }, this.propertyChangedUpdateDelay);
    } else {
      //console.log('changedProduct has no properties...');
    }
  }

  isQuantityChanged() {
    return this.quantity !== this.config.quantity;
  }

  quantityChanged(value) {
    if (value !== this.quantity) {
      this.quantity = value;
      this.refreshResetBtn();
    }
  }

  propertyChanged(propertyName, value, changedElement, spinnerElement) {
    this.product[propertyName] = value;
    if ((!this.changedProduct[propertyName]) || value !== this.changedProduct[propertyName]) {
      //console.log('Property changed...');
      if (value !== this.origProduct[propertyName]) {
        //console.log(`Assigning ${propertyName}=${value}...`);
        this.changedProduct[propertyName] = value;
      } else {
        //console.log(`Removing ${propertyName}...`);
        delete this.changedProduct[propertyName];
      }
      // DONE: Clear any pending server updates here...
      if (this.abortController) {
        this.abortController.abort();
      }
      this.invalidatePropertyChangedTimer(changedElement, spinnerElement);
    } else {
      //console.log('Property unchanged...');
    }
  }

  async resetProduct(isUserDriven, changedElement) {
    this.disableResetBtn(); // Prevent re-entrancy (multiple clicks)
    this.designerUpdateFlag.clear(); // Allows us to wait for update completion before adding to cart
    this.toggleProductSpinner(true, changedElement);
    try {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      try {
        this.quantity = this.config.quantity;
        let details;
        if (this.config.isDesignerProduct) {
          // The saved product from the cart (if present). If no cart, then saved and default products are the same:
          details = await this.designerService.updateProduct(this.config.savedProduct, signal, /*resetSession:*/ true);
        } else {
          details = await this.productService.getVariantProductDetails(this.config.productId, this.config.savedProduct.variantId, signal);
        }
        /* 1/24/20: We have a case where the savedProduct.DataVer and details.product.DataVer are
         * different. Let's try a simple fix: */
        this.config.savedProduct.dataVer = details.product.dataVer;
        this.config.defaultProduct.dataVer = details.product.dataVer;
        //console.log('Received updated product details:');
        //console.log(details);
        this.productDetailsChanged(details, null, true);
      } catch (ex) {
        if (ex.name === 'AbortError') {
          /* A promise (i.e. fetch call) was aborted (see AbortController.abort() method). At this
           * point the only use case is when the user makes another change before we've received a
           * response from the server for the previous change. 3/9/2021. */
          console.log('Designer Service call to reset the product was aborted due to user making more changes!');
        } else {
          console.error('Unknown error during call to designerService.updateProduct: ', ex);
          // Swallow the exception, but report it:
          GlobalErrorHandler.HandleException(ex);
        }
        this.enableResetBtn(); // We disabled it at the top of the method (only enable on exception though)
      }
      /*
      if (!this.isSavedProduct) {
        // Should never happen!!!
        const savedProductStr = JSON.stringify(this.config.savedProduct, null, '  ');
        const productStr = JSON.stringify(this.product, null, '  ');
        if (savedProductStr != productStr) {
          // Should never happen!!!
          console.log(savedProductStr);
          console.log(productStr);
        }
      }
      */
    } finally {
      this.designerUpdateFlag.set();
      this.toggleProductSpinner(false, changedElement);
    }
  }

  //#endregion Handling changed product properties....................

  // This method is called from the main view file (cshtml)
  async display() {
    // Intentionally left empty. Subclasses may override and add functionality here
  }
}

export class ValidationException extends Error {
  constructor(message) {
    super(message);
  }
}
